import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AutomationRuleInput, WeatherTrigger } from '@/lib/types';

export const automationKeys = {
  all: (jobId: string) => ['jobs', jobId, 'automation'] as const,
  overview: (jobId: string) => ['jobs', jobId, 'automation', 'overview'] as const,
  weather: (jobId: string) => ['jobs', jobId, 'automation', 'weather'] as const,
  runs: (jobId: string, ruleId?: string) => ['jobs', jobId, 'automation', 'runs', ruleId ?? 'all'] as const,
  preview: (jobId: string, template: string, lookahead: number, trigger?: WeatherTrigger) => ['jobs', jobId, 'automation', 'preview', template, lookahead, trigger] as const,
};

export function useAutomation(jobId: string) {
  return useQuery({ queryKey: automationKeys.overview(jobId), queryFn: () => api.automation.overview(jobId) });
}

export function useJobWeather(jobId: string, enabled: boolean) {
  return useQuery({ queryKey: automationKeys.weather(jobId), queryFn: () => api.automation.weather(jobId), enabled, staleTime: 5 * 60_000 });
}

export function useAutomationRuns(jobId: string, ruleId?: string) {
  return useQuery({ queryKey: automationKeys.runs(jobId, ruleId), queryFn: () => api.automation.runs(jobId, { ruleId, limit: 50 }), placeholderData: keepPreviousData });
}

export function useRulePreview(jobId: string, template: string, lookahead: number, trigger: WeatherTrigger | undefined, enabled: boolean) {
  return useQuery({
    queryKey: automationKeys.preview(jobId, template, lookahead, trigger),
    queryFn: ({ signal }) => api.automation.preview(jobId, template, lookahead, trigger, signal),
    enabled: enabled && template.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useAutomationMutations(jobId: string) {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: automationKeys.all(jobId) }), qc.invalidateQueries({ queryKey: ['jobs', jobId, 'messages'] })]);
  const create = useMutation({ mutationFn: (body: AutomationRuleInput) => api.automation.createRule(jobId, body), onSuccess: () => invalidate() });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<AutomationRuleInput> }) => api.automation.updateRule(jobId, id, body), onSuccess: () => invalidate() });
  const remove = useMutation({ mutationFn: (id: string) => api.automation.deleteRule(jobId, id), onSuccess: () => invalidate() });
  const evaluate = useMutation({ mutationFn: ({ id, force }: { id: string; force?: boolean }) => api.automation.evaluate(jobId, id, force), onSuccess: () => invalidate() });
  const refreshWeather = useMutation({ mutationFn: () => api.automation.weather(jobId, true), onSuccess: (data) => qc.setQueryData(automationKeys.weather(jobId), data) });
  return { create, update, remove, evaluate, refreshWeather };
}
